/**
 * Australian Age Pension Calculator
 * Aligns with current Australian pension rules and rates (2024)
 */

export interface AustralianPensionRates {
  year: number;
  single: {
    maximum: number;
    assetsTestThreshold: number;
    assetsTestFreeArea: number;
    incomeTestThreshold: number;
    incomeTestFreeArea: number;
  };
  couple: {
    maximum: number;
    assetsTestThreshold: number;
    assetsTestFreeArea: number;
    incomeTestThreshold: number;
    incomeTestFreeArea: number;
  };
}

export interface PensionEligibilityResult {
  isEligible: boolean;
  fullPensionAmount: number;
  reducedPensionAmount: number;
  assetsTestReduction: number;
  incomeTestReduction: number;
  limitingTest: 'assets' | 'income' | 'none';
  homeOwnerStatus: boolean;
}

export class AustralianPensionCalculator {
  // Current rates as of March 2024 (updated biannually)
  private static readonly CURRENT_RATES: AustralianPensionRates = {
    year: 2024,
    single: {
      maximum: 1020.60 * 26, // $1,020.60 per fortnight
      assetsTestThreshold: 691250, // Homeowner threshold
      assetsTestFreeArea: 314000, // Free area for homeowners
      incomeTestThreshold: 204, // Per fortnight
      incomeTestFreeArea: 204
    },
    couple: {
      maximum: 1538.60 * 26, // Combined for couple $1,538.60 per fortnight
      assetsTestThreshold: 1036500, // Homeowner threshold
      assetsTestFreeArea: 470000, // Free area for homeowners
      incomeTestThreshold: 360, // Combined per fortnight
      incomeTestFreeArea: 360
    }
  };

  // Non-homeowner additions
  private static readonly NON_HOMEOWNER_ADDITION = {
    single: 377250,
    couple: 377250
  };

  /**
   * Calculate age pension entitlement using current Australian rules
   */
  static calculatePensionEntitlement(
    totalAssets: number,
    totalIncome: number,
    isCouple: boolean = false,
    isHomeowner: boolean = true,
    partnerIncome: number = 0,
    partnerAssets: number = 0
  ): PensionEligibilityResult {
    const rates = this.CURRENT_RATES;
    const applicableRates = isCouple ? rates.couple : rates.single;

    // Adjust thresholds for non-homeowners
    const assetsThreshold = isHomeowner 
      ? applicableRates.assetsTestThreshold
      : applicableRates.assetsTestThreshold + this.NON_HOMEOWNER_ADDITION[isCouple ? 'couple' : 'single'];

    const assetsFreeArea = isHomeowner
      ? applicableRates.assetsTestFreeArea
      : applicableRates.assetsTestFreeArea + this.NON_HOMEOWNER_ADDITION[isCouple ? 'couple' : 'single'];

    // Calculate total household figures for couples
    const householdAssets = totalAssets + (isCouple ? partnerAssets : 0);
    const householdIncome = totalIncome + (isCouple ? partnerIncome : 0);

    // Assets test calculation
    const assetsTestReduction = this.calculateAssetsTestReduction(
      householdAssets,
      assetsFreeArea,
      assetsThreshold,
      isCouple
    );

    // Income test calculation  
    const incomeTestReduction = this.calculateIncomeTestReduction(
      householdIncome,
      applicableRates.incomeTestFreeArea,
      isCouple
    );

    // Apply the more restrictive test
    const totalReduction = Math.max(assetsTestReduction, incomeTestReduction);
    const reducedPensionAmount = Math.max(0, applicableRates.maximum - totalReduction);

    // Determine limiting test
    let limitingTest: 'assets' | 'income' | 'none' = 'none';
    if (reducedPensionAmount === 0) {
      limitingTest = assetsTestReduction > incomeTestReduction ? 'assets' : 'income';
    } else if (assetsTestReduction > incomeTestReduction) {
      limitingTest = 'assets';
    } else if (incomeTestReduction > assetsTestReduction) {
      limitingTest = 'income';
    }

    return {
      isEligible: reducedPensionAmount > 0,
      fullPensionAmount: applicableRates.maximum,
      reducedPensionAmount,
      assetsTestReduction,
      incomeTestReduction,
      limitingTest,
      homeOwnerStatus: isHomeowner
    };
  }

  /**
   * Calculate reduction under assets test
   */
  private static calculateAssetsTestReduction(
    totalAssets: number,
    freeArea: number,
    threshold: number,
    isCouple: boolean
  ): number {
    if (totalAssets <= freeArea) {
      return 0;
    }

    if (totalAssets >= threshold) {
      // No pension if assets exceed threshold
      return Number.MAX_SAFE_INTEGER;
    }

    // Reduction rate: $3 per fortnight for every $1,000 over free area
    // Annual reduction: $3 * 26 / $1,000 = $0.078 per dollar
    const excessAssets = totalAssets - freeArea;
    return excessAssets * 0.078;
  }

  /**
   * Calculate reduction under income test
   */
  private static calculateIncomeTestReduction(
    totalIncome: number,
    freeArea: number,
    isCouple: boolean
  ): number {
    // Convert annual income to fortnightly
    const fortnightlyIncome = totalIncome / 26;

    if (fortnightlyIncome <= freeArea) {
      return 0;
    }

    // Income test: pension reduces by 50 cents for every dollar over free area
    const excessIncome = fortnightlyIncome - freeArea;
    const fortnightlyReduction = excessIncome * 0.5;
    
    // Convert back to annual reduction
    return fortnightlyReduction * 26;
  }

  /**
   * Project pension rates with indexation
   */
  static projectPensionRates(years: number): AustralianPensionRates[] {
    const projections: AustralianPensionRates[] = [];
    const baseRates = this.CURRENT_RATES;
    
    // CPI indexation assumed at 2.5% annually
    const indexationRate = 0.025;

    for (let year = 0; year <= years; year++) {
      const inflationFactor = Math.pow(1 + indexationRate, year);
      
      projections.push({
        year: baseRates.year + year,
        single: {
          maximum: baseRates.single.maximum * inflationFactor,
          assetsTestThreshold: baseRates.single.assetsTestThreshold * inflationFactor,
          assetsTestFreeArea: baseRates.single.assetsTestFreeArea * inflationFactor,
          incomeTestThreshold: baseRates.single.incomeTestThreshold * inflationFactor,
          incomeTestFreeArea: baseRates.single.incomeTestFreeArea * inflationFactor
        },
        couple: {
          maximum: baseRates.couple.maximum * inflationFactor,
          assetsTestThreshold: baseRates.couple.assetsTestThreshold * inflationFactor,
          assetsTestFreeArea: baseRates.couple.assetsTestFreeArea * inflationFactor,
          incomeTestThreshold: baseRates.couple.incomeTestThreshold * inflationFactor,
          incomeTestFreeArea: baseRates.couple.incomeTestFreeArea * inflationFactor
        }
      });
    }

    return projections;
  }

  /**
   * Get deeming rates for financial investments
   */
  static getDeemingRates() {
    return {
      lowerRate: 0.0025, // 0.25% per annum
      higherRate: 0.0225, // 2.25% per annum
      threshold: {
        single: 62600,
        couple: 103800
      }
    };
  }

  /**
   * Calculate deemed income for assets test
   */
  static calculateDeemedIncome(
    financialAssets: number,
    isCouple: boolean = false
  ): number {
    const rates = this.getDeemingRates();
    const threshold = isCouple ? rates.threshold.couple : rates.threshold.single;

    if (financialAssets <= threshold) {
      return financialAssets * rates.lowerRate;
    }

    return (threshold * rates.lowerRate) + 
           ((financialAssets - threshold) * rates.higherRate);
  }
}